import { type ReactNode } from 'react';
import { TrendingUp, TrendingDown, Target, Flame } from 'lucide-react';

type Props = {
  weekDiff: number;
  nextRankGap: number | null;
  streak: number;
};

const CARD =
  'flex items-center gap-sm rounded-xl bg-white/[0.02] px-base py-sm';
const ICON = 'shrink-0 rounded-lg p-1.5';
const LABEL = 'text-[10px] text-text-tertiary';
const VALUE = 'text-[13px] font-medium text-text-primary';

function WeekComparison({ diff }: { diff: number }): ReactNode {
  const positive = diff >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const color = positive
    ? 'text-success bg-success/10'
    : 'text-warning bg-warning/10';
  const text = positive
    ? `${diff} creator${diff !== 1 ? 's' : ''} más que la semana pasada`
    : `${Math.abs(diff)} creator${Math.abs(diff) !== 1 ? 's' : ''} menos que la semana pasada`;
  return (
    <div className={CARD}>
      <span className={`${ICON} ${color}`}>
        <Icon size={14} />
      </span>
      <div>
        <p className={LABEL}>vs. semana anterior</p>
        <p className={VALUE}>{text}</p>
      </div>
    </div>
  );
}

function NextRank({ gap }: { gap: number }): ReactNode {
  return (
    <div className={CARD}>
      <span className={`${ICON} text-info bg-info/10`}>
        <Target size={14} />
      </span>
      <div>
        <p className={LABEL}>Siguiente puesto</p>
        <p className={VALUE}>
          A {gap} creator{gap !== 1 ? 's' : ''} del siguiente puesto
        </p>
      </div>
    </div>
  );
}

function Streak({ days }: { days: number }): ReactNode {
  if (days < 1) return null;
  return (
    <div className={CARD}>
      <span className={`${ICON} text-brand-coral bg-brand-coral/10`}>
        <Flame size={14} />
      </span>
      <div>
        <p className={LABEL}>Racha</p>
        <p className={VALUE}>
          {days} día{days !== 1 ? 's' : ''} consecutivo{days !== 1 ? 's' : ''}
        </p>
      </div>
    </div>
  );
}

export function MotivationalCards({
  weekDiff,
  nextRankGap,
  streak,
}: Props): ReactNode {
  return (
    <div className="grid gap-sm md:grid-cols-3">
      <WeekComparison diff={weekDiff} />
      {nextRankGap !== null && <NextRank gap={nextRankGap} />}
      <Streak days={streak} />
    </div>
  );
}
